import RolesModal, { RoleState } from '@app/components/userManagement/organizationUsersTable/RolesModal';
import { OrganizationalUser } from '@app/domain/userManagement/organizationUsers';
import { UserRoleType } from '@app/domain/userManagement/roleTypes';
import { RolesOfUsersSaveProgress, UpdateWebUserRolesRequest } from '@app/domain/userManagement/users';
import Loadable from '@app/utils/Loadable';
import { pluralize } from '@app/utils/stringUtils';
import { Alert, Tooltip } from 'antd';
import React, { FC, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { CaptionStartTransparentBlack600 } from '@app/components/Text';
import { MAX_CHANGES_PER_SAVE } from '@app/domain/uiConsts';

interface Props {
  organizationalUsers: OrganizationalUser[];
  userRoleTypes: Loadable<UserRoleType[]>;
  saveRolesOfUsers(
    organizationalUsers: OrganizationalUser[],
    change: UpdateWebUserRolesRequest,
    progress: RolesOfUsersSaveProgress,
  ): Promise<void>;
  onDone(): void;
}

const MAX_NAMED_GLOBAL_ROLE_HOLDERS = 3;
const MAX_NAMED_SELECTED_USERS = 3;

interface SaveProgress {
  savedCount: number;
  totalCount: number;
}

const SelectedUsersRolesModal: FC<Props> = ({ organizationalUsers, userRoleTypes, saveRolesOfUsers, onDone }) => {
  const savedUsersOfChangeRef = useRef<{ changeKey: string; tenantUserIds: Set<string> } | null>(null);
  const [saveProgress, setSaveProgress] = useState<SaveProgress | null>(null);

  async function saveChange(change: UpdateWebUserRolesRequest): Promise<void> {
    // Saving the same change again skips the users an earlier, partly failed attempt already updated
    const changeKey = JSON.stringify([[...change.addedWebAppRoleIds].sort(), [...change.removedWebAppRoleIds].sort()]);
    if (savedUsersOfChangeRef.current?.changeKey !== changeKey) {
      savedUsersOfChangeRef.current = { changeKey, tenantUserIds: new Set() };
    }
    const savedTenantUserIds = savedUsersOfChangeRef.current.tenantUserIds;
    const remainingUsers = organizationalUsers.filter(
      (organizationalUser) => !savedTenantUserIds.has(organizationalUser.tenantUserId),
    );
    const totalCount = organizationalUsers.length;

    setSaveProgress({ savedCount: savedTenantUserIds.size, totalCount });
    try {
      await saveRolesOfUsers(remainingUsers, change, {
        alreadySavedCount: savedTenantUserIds.size,
        onUsersSaved: (tenantUserIds): void => {
          tenantUserIds.forEach((tenantUserId) => savedTenantUserIds.add(tenantUserId));
          setSaveProgress({ savedCount: savedTenantUserIds.size, totalCount });
        },
      });
    } finally {
      setSaveProgress(null);
    }
  }

  function getRoleState(roleId: string, roleType: UserRoleType | undefined): RoleState {
    // Saving would touch only the organizational role, leaving the global one in place
    const globalRoleHolders = organizationalUsers.filter((organizationalUser) =>
      organizationalUser.globalAppRoleIds.includes(roleId),
    );
    if (globalRoleHolders.length) {
      return { isDisabled: true, tooltip: `${describeGlobalRoleHolders(globalRoleHolders)} cannot be changed here` };
    }

    // The organization can't be granted it, so it may only be taken away
    if (!roleType) {
      return {
        isDisabled: false,
        isRemovalOnly: true,
        tooltip: 'The organization does not have the service for this role, so it can only be removed',
      };
    }

    return { isDisabled: false, tooltip: null };
  }

  return (
    <RolesModal
      title={
        <>
          Permissions - {organizationalUsers.length} selected {pluralize('user', organizationalUsers.length)}
        </>
      }
      subtitle={
        <>
          <SelectedUsersNames organizationalUsers={organizationalUsers} />
          {saveProgress && saveProgress.totalCount > MAX_CHANGES_PER_SAVE && (
            <CaptionStartTransparentBlack600.div data-testid='selected-users-roles-modal-progress'>
              Saving {saveProgress.savedCount.toLocaleString()} of {saveProgress.totalCount.toLocaleString()} users…
            </CaptionStartTransparentBlack600.div>
          )}
        </>
      }
      elementIds={{
        card: 'selected-users-roles-modal',
        search: 'selected-users-roles-search',
        table: 'selected-users-roles-table',
        close: 'btn-selected-users-roles-modal-close',
        cancel: 'btn-selected-users-roles-modal-cancel',
        save: 'btn-selected-users-roles-modal-save',
      }}
      contentColumnTitle='Roles'
      banner={
        <Alert
          type='info'
          showIcon
          message='A mixed role is held by only some of the selected users. Left mixed, it stays as is.'
        />
      }
      roleTypes={userRoleTypes}
      initialActiveRoleIdsPerUser={organizationalUsers.map((organizationalUser) => organizationalUser.allActiveAppRoleIds)}
      canEdit
      getRoleState={getRoleState}
      saveChange={saveChange}
      onDone={onDone}
    />
  );
};

const SelectedUsersNames: FC<{ organizationalUsers: OrganizationalUser[] }> = ({ organizationalUsers }) => {
  const nameOf = (organizationalUser: OrganizationalUser): string => organizationalUser.name ?? organizationalUser.tenantUserId;
  const othersCount = organizationalUsers.length - MAX_NAMED_SELECTED_USERS;

  return (
    <CaptionStartTransparentBlack600.div data-testid='selected-users-roles-modal-names'>
      {organizationalUsers.slice(0, MAX_NAMED_SELECTED_USERS).map(nameOf).join(', ')}
      {othersCount > 0 && (
        <>
          {', '}
          <Tooltip
            title={
              <AllNames>
                {organizationalUsers.map((organizationalUser) => (
                  <div key={organizationalUser.tenantUserId}>{nameOf(organizationalUser)}</div>
                ))}
              </AllNames>
            }
          >
            <OthersCount data-testid='selected-users-roles-modal-names-more'>+{othersCount} more</OthersCount>
          </Tooltip>
        </>
      )}
    </CaptionStartTransparentBlack600.div>
  );
};

const AllNames = styled.div`
  max-height: 300px;
  overflow-y: auto;
`;

const OthersCount = styled.span`
  text-decoration: underline dotted;
  cursor: default;
`;

function describeGlobalRoleHolders(globalRoleHolders: OrganizationalUser[]): string {
  const names = globalRoleHolders
    .slice(0, MAX_NAMED_GLOBAL_ROLE_HOLDERS)
    .map((organizationalUser) => organizationalUser.name ?? organizationalUser.tenantUserId);
  const othersCount = globalRoleHolders.length - names.length;

  return `A global role of ${names.join(', ')}${othersCount ? ` and ${othersCount} more` : ''}, so it`;
}

export default SelectedUsersRolesModal;
